import { NONE_VALUE } from '@/constants/answers';
import { type NationalClientIdentifierFormats, nationalClientIdentifierFormatsQueryOptions } from '@/hooks/queries/use-national-client-identifier-formats';
import i18n from '@/lib/i18n';
import { queryClient } from '@/lib/query-client';
import { reportInvalidIdentifier } from '@/utils/identifier';
import { appendNationalClientIdentifierExample, getNationalClientIdentifierTypeLabel, NationalClientIdentifierType } from '@/utils/national-client-identifier';

type FormattedNationalClientIdentifierType = Exclude<NationalClientIdentifierType, typeof NationalClientIdentifierType.CONCAT>;

type NationalClientIdentifierFormatParts = {
  country: string | undefined;
  identifier?: string | undefined;
  identifierType: NationalClientIdentifierType | typeof NONE_VALUE | undefined;
};

// CONCAT is derived from the individual's own details rather than entered, so it has no format to match.
const hasIdentifierFormat = (identifierType: NationalClientIdentifierFormatParts['identifierType']): identifierType is FormattedNationalClientIdentifierType =>
  !!identifierType && identifierType !== NONE_VALUE && identifierType !== NationalClientIdentifierType.CONCAT;

export const getNationalClientIdentifierExample = (
  formats: NationalClientIdentifierFormats | undefined,
  { country, identifierType }: Omit<NationalClientIdentifierFormatParts, 'identifier'>,
) => (formats && country && hasIdentifierFormat(identifierType) ? formats.examples[country]?.[identifierType] : undefined);

export const getNationalClientIdentifierFormatError = async ({ country, identifier, identifierType }: NationalClientIdentifierFormatParts) => {
  if (!country || !identifier || !hasIdentifierFormat(identifierType)) return;

  const formats = await queryClient.ensureQueryData(nationalClientIdentifierFormatsQueryOptions);
  // The API uppercases the identifier before matching, and the patterns are uppercase-only.
  const isValidFormat = formats.patterns[country]?.[identifierType]?.test(identifier.toUpperCase()) ?? true;
  if (isValidFormat) return;

  return appendNationalClientIdentifierExample(
    i18n.t('nationalClientIdentifier.identifier.errors.invalid', {
      type: getNationalClientIdentifierTypeLabel(identifierType, country),
    }),
    getNationalClientIdentifierExample(formats, { country, identifierType }),
  );
};

export const reportNationalClientIdentifierFormatError = async ({ country, identifier, identifierType }: NationalClientIdentifierFormatParts) => {
  if (!country || !identifier || !(await getNationalClientIdentifierFormatError({ country, identifier, identifierType }))) return;

  reportInvalidIdentifier('NCI_VALIDATION_FAILED', { country, identifier, identifierType });
};
